import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Zoom,
  Fade,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import * as Icons from '@mui/icons-material';
import { useContent } from '../contexts/ContentContext';
import { ContentSection, NewsArticle } from '../services/content';

// Enhanced VideoBackground component with video and image support
const VideoBackground = ({ hero }: { hero: any }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...(hero.backgroundImage && !hero.backgroundVideo && {
        backgroundImage: `url(${hero.backgroundImage.src})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#2c3e50', // Fallback color
      }),
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1,
      },
    }}
  >
    {hero.backgroundVideo && (
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="none"
        onError={(e) => {
          console.error('Video failed to load:', e);
          console.error('Video src:', hero.backgroundVideo.src);
        }}
        onLoadStart={() => console.log('Video loading started')}
        onCanPlay={() => console.log('Video can play')}
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      >
        <source src={hero.backgroundVideo.src} type={hero.backgroundVideo.type} />
        Your browser does not support the video tag.
      </video>
    )}
    <Box
      sx={{
        position: 'relative',
        zIndex: 2,
        textAlign: 'center',
        color: 'white',
        px: 3,
        maxWidth: '800px',
        animation: 'fadeInUp 1s ease-out',
      }}
    >
      <Typography 
        variant="h2" 
        component="h1" 
        sx={{ 
          fontWeight: 700, 
          mb: 1,
          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          fontSize: { xs: '2.2rem', md: '3.2rem' }
        }}
      >
        {hero.title}
      </Typography>
      <Typography 
        variant="h4" 
        component="h2" 
        sx={{ 
          fontWeight: 500, 
          mb: 3,
          color: 'secondary.main',
          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          fontSize: { xs: '1.5rem', md: '2rem' }
        }}
      >
        {hero.subtitle}
      </Typography>
      
      <Typography 
        variant="h5" 
        sx={{ 
          mb: 4,
          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
          fontSize: { xs: '1.2rem', md: '1.5rem' }
        }}
      >
        {hero.description}
      </Typography>
      
      <Box sx={{ mt: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: 2 }}>
        <Button 
          variant="contained" 
          color="secondary" 
          size="large"
          component={RouterLink}
          to={hero.primaryButton.link}
          sx={{ 
            fontSize: '1.1rem',
            px: 4,
            py: 1.5,
            fontWeight: 600,
            color: '#fff',
          }}
        >
          {hero.primaryButton.text}
        </Button>
        
        <Button 
          variant="outlined" 
          color="inherit" 
          size="large" 
          component="a"
          href={hero.secondaryButton.link}
          target="_blank"
          sx={{ 
            fontSize: '1.1rem',
            px: 4,
            py: 1.5,
            borderColor: 'white',
          }}
        >
          {hero.secondaryButton.text}
        </Button>
      </Box>
    </Box>
  </Box>
);

// Enhanced SectionTitle with animation
const SectionTitle: React.FC<{children: React.ReactNode, align?: "left" | "center" | "right", color?: string}> = ({children, align = "left", color}) => (
  <Typography 
    variant="h4" 
    component="h2" 
    sx={{ 
      fontWeight: 600, 
      mb: 3, 
      color: color || 'primary.main',
      position: 'relative',
      textAlign: align,
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -8,
        left: align === "center" ? '50%' : 0,
        transform: align === "center" ? 'translateX(-50%)' : 'none',
        width: 60,
        height: 3,
        backgroundColor: 'secondary.main',
        transition: 'width 0.3s ease-in-out',
      },
    }}
  >
    {children}
  </Typography>
);

// Dynamic icon component
const DynamicIcon: React.FC<{ iconName: string; sx?: any }> = ({ iconName, sx }) => {
  const IconComponent = (Icons as any)[iconName] || Icons.Info;
  return <IconComponent sx={sx} />;
};

// Content section component
const ContentSectionComponent: React.FC<{ section: ContentSection; index: number; onComingSoon: () => void }> = ({ section, index, onComingSoon }) => {
  const theme = useTheme();
  
  return (
    <Fade in={true} timeout={1000} style={{ transitionDelay: `${index * 200}ms` }}>
      <Box sx={{ 
        py: 10, 
        backgroundColor: section.backgroundColor || '#f7f7f7',
        color: section.textColor || 'inherit',
        position: 'relative', 
        overflow: 'hidden',
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            <SectionTitle color={section.textColor}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <DynamicIcon iconName={section.icon} sx={{ mr: 1, color: 'secondary.main' }} />
                {section.title}
              </Box>
            </SectionTitle>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: section.image ? 'row' : 'column' }, 
              alignItems: 'center', 
              mb: 4, 
              gap: 4 
            }}>
              <Box sx={{ flex: '1 1 auto' }}>
                {section.content.map((paragraph, idx) => (
                  <Typography 
                    key={idx}
                    variant="body1" 
                    paragraph
                    sx={{ color: section.textColor || 'inherit' }}
                    dangerouslySetInnerHTML={{ __html: paragraph }}
                  />
                ))}
              </Box>
              {section.image && (
                <Box
                  component="img"
                  src={section.image.src}
                  alt={section.image.alt}
                  sx={{
                    width: { xs: '100%', md: '270px' },
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                    flex: '0 0 auto',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.03)',
                    }
                  }}
                />
              )}
            </Box>
            
            {(section.button || section.buttons) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4 }}>
                {section.button && (
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    {...(section.button.link === '#coming-soon' 
                      ? { onClick: onComingSoon }
                      : { 
                          href: section.button.link, 
                          target: "_blank", 
                          rel: "noopener noreferrer" 
                        }
                    )}
                    sx={{ fontWeight: 500 }}
                  >
                    {section.button.text}
                  </Button>
                )}
                {section.buttons?.map((button, idx) => (
                  <Button 
                    key={idx}
                    variant="outlined" 
                    color="primary" 
                    {...(button.link === '#coming-soon' 
                      ? { onClick: onComingSoon }
                      : { 
                          href: button.link, 
                          target: "_blank", 
                          rel: "noopener noreferrer" 
                        }
                    )}
                    sx={{ fontWeight: 500 }}
                  >
                    {button.text}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </Fade>
  );
};

// News item component
const NewsItem = ({ article }: { article: NewsArticle }) => {
  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
      }
    }}>
      <CardMedia
        component="img"
        height="160"
        image={article.image}
        alt={article.title}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {article.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {article.description}
        </Typography>
        <Button 
          endIcon={<Icons.ArrowForward />} 
          href={article.link} 
          target="_blank"
          color="primary"
          sx={{ padding: 0 }}
        >
          {article.linkText}
        </Button>
      </CardContent>
    </Card>
  );
};

// Main DynamicAbout component
const DynamicAbout: React.FC = () => {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));
  const { aboutContent, isLoading, error } = useContent();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleComingSoon = () => {
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error || !aboutContent) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error || 'Failed to load content'}
        </Alert>
      </Container>
    );
  }
  
  return (
    <>
      {/* Hero Section */}
      <Box sx={{ position: 'relative', minHeight: '100vh', mb: 8 }}>
        <VideoBackground hero={aboutContent.hero} />
        <Box sx={{ height: '100vh' }} />
      </Box>

      {/* Content Sections */}
      {aboutContent.sections.map((section, index) => (
        <ContentSectionComponent 
          key={section.id} 
          section={section} 
          index={index} 
          onComingSoon={handleComingSoon}
        />
      ))}

      {/* News Section */}
      {aboutContent.news.articles.length > 0 && (
        <Fade in={true} timeout={800}>
          <Box sx={{ py: 10, backgroundColor: 'white' }}>
            <Container maxWidth="lg">
              <SectionTitle align="center">{aboutContent.news.title}</SectionTitle>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                justifyContent: 'center', 
                gap: 4, 
                mt: 2,
                maxWidth: '100%'
              }}>
                {aboutContent.news.articles.map((article, index) => (
                  <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }} key={index}>
                    <Box sx={{ 
                      flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(33.333% - 16px)', lg: '1 1 calc(25% - 16px)' },
                      maxWidth: { xs: '100%', sm: '400px', md: '350px', lg: '300px' },
                      minWidth: { xs: '280px', sm: '280px', md: '280px', lg: '280px' }
                    }}>
                      <NewsItem article={article} />
                    </Box>
                  </Zoom>
                ))}
              </Box>
            </Container>
          </Box>
        </Fade>
      )}

      {/* Coming Soon Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message="Coming soon!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default DynamicAbout; 